import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LocationSearch } from "@/components/LocationSearch";
import { Req, ERR_CLS } from "@/components/RequiredMark";
import { Sparkles, Sprout, Crown } from "lucide-react";
import { toast } from "sonner";

const COLLECTIVE_TYPE_OPTIONS = [
  ["volunteering", "Volunteering", "Organise volunteer work"],
  ["social", "Social", "Meetups & get-togethers"],
  ["both", "Both", "A mix of the two"],
];

// ────────────────────────────────────────────────────────────────────────────
// Start-a-collective dialog — extracted from pages/Collectives.jsx in the
// Phase 15 component split; behaviour unchanged.
// ────────────────────────────────────────────────────────────────────────────
export function StartCollectiveDialog({ onCreated, triggerVariant = "default", autoOpen = false, initialArea = null }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  // Landing "Start a community" CTA deep-links /collectives?start=1
  useEffect(() => { if (autoOpen) setOpen(true); }, [autoOpen]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [area, setArea] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [radius, setRadius] = useState(5);
  const [motivation, setMotivation] = useState("");
  const [membershipMode, setMembershipMode] = useState("open");
  const [ctype, setCtype] = useState("volunteering");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [nearby, setNearby] = useState([]);
  const clearErr = (k) => setErrors((prev) => (prev[k] ? { ...prev, [k]: false } : prev));

  // Phase 17.5 (duplicates): once the area is pinned, surface any collective
  // already active within 3 miles — a soft "join instead?" prompt, never a block.
  useEffect(() => {
    if (lat == null || lng == null) { setNearby([]); return undefined; }
    let cancelled = false;
    api.collectives({ near_lat: lat, near_lng: lng, near_miles: 3 })
      .then((rows) => { if (!cancelled) setNearby(rows.slice(0, 3)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lat, lng]);

  // Item 5 (17-C): postcode empty state opens this dialog prefilled with the
  // searched area — only when the field is still untouched.
  useEffect(() => {
    if (open && initialArea && !area && initialArea.lat != null) {
      setArea(initialArea.label || "");
      setLat(initialArea.lat);
      setLng(initialArea.lng);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- prefill on open only, never clobber typed input

  const submit = async () => {
    const missing = {
      name: !name.trim(),
      description: !description.trim(),
      motivation: !motivation.trim(),
      area: lat == null || lng == null,
    };
    setErrors(missing);
    if (missing.name || missing.description || missing.motivation) return toast.error("Fill the required fields — highlighted in red");
    if (missing.area) return toast.error("Pick your area from the location suggestions");
    setSaving(true);
    try {
      await api.applyCollective({ name, description, area_label: area, lat, lng, radius_miles: radius, motivation, membership_mode: membershipMode, collective_type: ctype });
      setOpen(false);
      toast.success("Application sent! Our team will review it and let you know.");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send application");
    }
    setSaving(false);
  };

  const trigger = triggerVariant === "empty" ? (
    <Button data-testid="collectives-empty-cta" onClick={(e) => { if (!user) { e.preventDefault(); nav("/login"); } }}
      className="rounded-full bg-terracotta hover:bg-terracotta-hover h-11 px-6">
      <Crown className="w-4 h-4 mr-2" /> Start a collective
    </Button>
  ) : (
    <Button data-testid="start-collective-btn"
      onClick={(e) => { if (!user) { e.preventDefault(); nav("/login"); } }}
      className="rounded-full bg-terracotta hover:bg-terracotta-hover h-11 px-6">
      <Crown className="w-4 h-4 mr-2" /> Start a collective
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="start-collective-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Apply to lead a collective</DialogTitle>
          <DialogDescription>
            Collectives connect neighbours in an area. Tell us about your patch and why you'd like to lead it — an admin will review your application.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Name<Req /></label>
            <Input data-testid="collective-name-input" value={name} onChange={(e) => { setName(e.target.value); clearErr("name"); }} placeholder="e.g. Hackney Helpers" className={`mt-2 h-11 ${errors.name ? ERR_CLS : ""}`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">What's it about?<Req /></label>
            <Textarea data-testid="collective-desc-input" value={description} onChange={(e) => { setDescription(e.target.value); clearErr("description"); }} rows={3} placeholder="A friendly group for anyone in the area who wants to pitch in locally." className={`mt-2 ${errors.description ? ERR_CLS : ""}`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Area — town or postcode<Req /></label>
            <div className="mt-2">
              <LocationSearch
                testId="collective-area-input"
                value={area}
                placeholder="e.g. Hackney, London or E8 1DY"
                inputClassName={errors.area ? ERR_CLS : ""}
                onSelect={(r) => { setArea(r.label); setLat(r.lat); setLng(r.lng); clearErr("area"); }}
                onTextChange={(v) => { setArea(v); setLat(null); setLng(null); }}
              />
            </div>
          </div>
          {nearby.length > 0 && (
            <div data-testid="dup-warning" className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 space-y-2">
              <p className="text-sm text-amber-900 font-medium">
                Good news — your area already has {nearby.length === 1 ? "a collective" : "collectives"}. Join instead?
              </p>
              {nearby.map((n) => (
                <Link
                  key={n.id}
                  data-testid={`dup-nearby-${n.id}`}
                  to={`/collectives/${n.id}`}
                  className="block text-sm text-forest font-medium underline underline-offset-2 hover:text-terracotta transition-colors"
                >
                  {n.name} · {n.distance_miles} mi away · {n.member_count} member{n.member_count !== 1 ? "s" : ""}
                </Link>
              ))}
              <p className="text-xs text-amber-800/80">
                You can still send your application — some areas support more than one.
              </p>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Radius</label>
              <span className="text-sm text-forest font-medium">{radius} miles</span>
            </div>
            <Slider data-testid="collective-radius-slider" value={[radius]} onValueChange={(v) => setRadius(v[0])} min={1} max={50} step={1} className="mt-3" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Why do you want to lead this collective?<Req /></label>
            <Textarea data-testid="collective-motivation-input" value={motivation} onChange={(e) => { setMotivation(e.target.value); clearErr("motivation"); }} rows={3} placeholder="I've lived here 10 years and want to bring neighbours together…" className={`mt-2 ${errors.motivation ? ERR_CLS : ""}`} />
          </div>
          <div data-testid="apply-collective-type">
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">What kind of collective?<Req /></label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {COLLECTIVE_TYPE_OPTIONS.map(([v, l, d]) => (
                <label
                  key={v}
                  className={`cursor-pointer min-w-0 px-2 py-3 rounded-lg border text-center transition-colors ${ctype === v ? "border-forest bg-forest/5" : "border-neutral-200 hover:border-forest/40"}`}
                >
                  <input
                    type="radio"
                    name="collective_type"
                    className="sr-only"
                    checked={ctype === v}
                    onChange={() => setCtype(v)}
                    data-testid={`collective-type-${v}`}
                  />
                  <span className="block font-medium text-forest text-sm break-words">{l}</span>
                  <span className="block text-[11px] text-neutral-500 mt-0.5 break-words">{d}</span>
                </label>
              ))}
            </div>
          </div>
          <div data-testid="apply-membership-mode">
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Membership approval</label>
            <div className="mt-2 space-y-2">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-neutral-200 hover:border-forest/40">
                <input type="radio" name="membership_mode" value="open" checked={membershipMode === "open"}
                  onChange={() => setMembershipMode("open")} data-testid="membership-mode-open" className="mt-1 accent-forest" />
                <span>
                  <span className="font-medium text-forest text-sm">Open</span>
                  <span className="block text-xs text-neutral-500">Anyone in the radius can join instantly.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-neutral-200 hover:border-forest/40">
                <input type="radio" name="membership_mode" value="review" checked={membershipMode === "review"}
                  onChange={() => setMembershipMode("review")} data-testid="membership-mode-review" className="mt-1 accent-forest" />
                <span>
                  <span className="font-medium text-forest text-sm">Leader review</span>
                  <span className="block text-xs text-neutral-500">You approve each request before someone joins.</span>
                </span>
              </label>
            </div>
          </div>
          <div className="flex justify-end">
            <Button data-testid="collective-apply-submit" onClick={submit} disabled={saving} className="rounded-full bg-forest hover:bg-forest-hover h-11 px-6">
              {saving ? "Sending…" : "Send application"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

